import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// 새로고침 시 로컬스토리지에서 초기값을 가져오는 함수
const getInitialUser = () => {
  const saved = localStorage.getItem('user');
  try {
    return saved ? JSON.parse(saved) : null; // _id 사용
  } catch {
    return null; // JSON 파싱 오류 시 null 반환
  }
};

const initialUser = getInitialUser();

// 🚩 [추가] 서버에서 내 최신 정보 가져오기 (Load User)
export const loadUser = createAsyncThunk('auth/loadUser', async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return rejectWithValue("No token");

    const res = await axios.get('https://the-pivot-logical-battleground.onrender.com/api/auth/me', {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

// 🚩 [신규] 특정 게시글 알림 읽음 처리 액션
export const markNotificationAsRead = createAsyncThunk('auth/markRead', async (postId, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('token');
    await axios.post('https://the-pivot-logical-battleground.onrender.com/api/auth/read-notification', 
      { postId }, 
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return postId; // 성공하면 읽은 postId 반환
  } catch (err) {
    return rejectWithValue(err.message);
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: initialUser,
    isAuthenticated: !!initialUser,
    isGuest: false,
    hasPaidEntry: false || initialUser?.hasPaidEntry,
    loading: false,
    error: null
  },
  reducers: {

    // 로그인 성공
    loginSuccess: (state, action) => {
      state.isGuest = false; // 로그인 시 게스트 해제
      const {user, token} = action.payload;
      
      if (!user || !token) {
        console.error("로그인 실패: 사용자 데이터 또는 토큰이 없습니다.");
        return;
      }

      state.user = user;
      state.token = token;
      state.isAuthenticated = true;
      state.hasPaidEntry = user.hasPaidEntry;

      localStorage.setItem('user', JSON.stringify(user));
      if (token) localStorage.setItem('token', token);
    },

    // 회원가입 성공 (회원가입 후 바로 로그인 페이지로 보내거나 자동 로그인 처리용)
    registerSuccess: (state, action) => {
      state.loading = false;
      // register.js에서 처리.
    },

    // 결제 성공
    paymentSuccess: (state) => {
      state.hasPaidEntry = true;
      if (state.user) {
        state.user.hasPaidEntry = true;
        localStorage.setItem('user', JSON.stringify(state.user));
      }
    },

    // 진영 정보 업데이트 리듀서
    updateFactionSuccess: (state, action) => {
      const { user, token } = action.payload;
      
      state.user = user;
      if (token) state.token = token;
      
      // 로컬스토리지도 함께 동기화
      localStorage.setItem('user', JSON.stringify(user));
      if (token) localStorage.setItem('token', token);
    },

    // 진영 정보 초기화 리듀서
    clearFaction: (state) => {
      if (state.user) {
        delete state.user.currentFaction;
        localStorage.setItem('user', JSON.stringify(state.user));
      }
    },

    // 포인트 및 가입 주제 업데이트 리듀서
    updatePoints: (state, action) => {
      if (state.user) {
        state.user.points = action.payload.points;
        if (action.payload.joinedTopics) {
          state.user.joinedTopics = action.payload.joinedTopics;
        }
        localStorage.setItem('user', JSON.stringify(state.user));
      }
    },

    // 🚩 [추가] 화면에서 빨간 점 즉시 없애기 & 로컬스토리지 동기화
    clearNotification: (state) => {
      if (state.user) {
        state.user.hasNewNotification = false;
        // 로컬스토리지도 업데이트해야 새로고침 전에도 유지됨
        localStorage.setItem('user', JSON.stringify(state.user));
      }
    },

    // 🚩 둘러보기 액션 추가
    setGuestMode: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.isGuest = true;
      state.hasPaidEntry = false;
    },

    // 로그아웃 리듀서
    logout: (state) => {
      state.isGuest = false;
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.hasPaidEntry = false;
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('isAuthenticated');
    }
  },

  // 🚩 [추가] loadUser 비동기 요청 처리
  extraReducers: (builder) => {
    builder
      .addCase(loadUser.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.user = action.payload; // 서버에서 받은 최신 유저 정보로 덮어쓰기!
        localStorage.setItem('user', JSON.stringify(action.payload)); // 로컬스토리지도 최신화
      })
      .addCase(loadUser.rejected, (state) => {
        // 토큰이 만료되었거나 유효하지 않으면 로그아웃 처리 가능 (선택사항)
        // state.isAuthenticated = false;
        // state.user = null;
      })
      // 🚩 [신규] 읽음 처리 성공 시, 리덕스 배열에서도 해당 ID 즉시 삭제
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        if (state.user && state.user.unreadPostIds) {
          state.user.unreadPostIds = state.user.unreadPostIds.filter(id => id !== action.payload);
          localStorage.setItem('user', JSON.stringify(state.user)); // 로컬스토리지 동기화
        }
      });
  }
});


export const { loginSuccess, registerSuccess, paymentSuccess, updateFactionSuccess, clearFaction, logout, updatePoints, clearNotification, setGuestMode } = authSlice.actions;
export default authSlice.reducer;